import JSZip from 'jszip';

export const ENTITLEMENT_UPLOAD_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
export const ENTITLEMENT_UPLOAD_MAX_ROWS = 5000;

export interface EntitlementUploadRow {
  cpf: string;
  cnpj: string;
  combo: string;
  quantity: number;
  rowNumber: number;
  errors: string[];
}

type Matrix = string[][];

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Erro ao ler arquivo'));
    reader.readAsText(blob);
  });
}

function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }
      reject(new Error('Erro ao ler arquivo XLSX'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Erro ao ler arquivo'));
    reader.readAsArrayBuffer(blob);
  });
}

function columnName(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function columnIndex(cellRef: string): number {
  const match = cellRef.match(/[A-Z]+/i);
  if (!match) return 0;
  return match[0].toUpperCase().split('').reduce((acc, char) => acc * 26 + char.charCodeAt(0) - 64, 0) - 1;
}

function inlineStringCell(ref: string, value: string, style = 1): string {
  return `<c r="${ref}" t="inlineStr" s="${style}"><is><t>${escapeXml(value)}</t></is></c>`;
}

function worksheetXml(rows: Matrix, includeInstruction: boolean): string {
  const rowXml = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((value, colIndex) => inlineStringCell(`${columnName(colIndex)}${rowIndex + 1}`, value, rowIndex === 0 ? 2 : 1))
        .join('');
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  const instruction = includeInstruction
    ? '<sheetViews><sheetView workbookViewId="0"/></sheetViews>'
    : '';

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
${instruction}
<cols>
  <col min="1" max="1" width="22" customWidth="1" style="1"/>
  <col min="2" max="2" width="18" customWidth="1" style="1"/>
  <col min="3" max="3" width="24" customWidth="1" style="1"/>
  <col min="4" max="4" width="20" customWidth="1" style="1"/>
</cols>
<sheetData>${rowXml}</sheetData>
</worksheet>`;
}

export async function createEntitlementUploadTemplateBlob(): Promise<Blob> {
  const zip = new JSZip();

  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`);

  zip.folder('_rels')?.file('.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`);

  zip.folder('docProps')?.file('core.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/">
  <dc:title>Template de Upload de Concessoes Reedy</dc:title>
</cp:coreProperties>`);

  zip.folder('docProps')?.file('app.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties">
  <Application>Reedy Admin</Application>
</Properties>`);

  zip.folder('xl')?.file('workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Upload" sheetId="1" r:id="rId1"/>
    <sheet name="Exemplo" sheetId="2" r:id="rId2"/>
  </sheets>
</workbook>`);

  zip.folder('xl')?.folder('_rels')?.file('workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`);

  zip.folder('xl')?.file('styles.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/></font>
  </fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="3">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="49" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`);

  zip.folder('xl')?.folder('worksheets')?.file(
    'sheet1.xml',
    worksheetXml([
      ['CNPJ', 'CPF', 'Código do Combo', 'Quantidade'],
    ], true),
  );

  zip.folder('xl')?.folder('worksheets')?.file(
    'sheet2.xml',
    worksheetXml([
      ['CNPJ', 'CPF', 'Código do Combo', 'Quantidade'],
      ['00000000000191', '00000000191', 'REEDY 30', '1'],
    ], false),
  );

  return zip.generateAsync({ type: 'blob', mimeType: XLSX_MIME_TYPE });
}

export async function downloadEntitlementUploadTemplate(): Promise<void> {
  const blob = await createEntitlementUploadTemplateBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'template-upload-concessoes-reedy.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function isSupportedEntitlementUploadFile(file: File): boolean {
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return ['.csv', '.xlsx'].includes(extension);
}

function normalizedHeader(value: string): string {
  return value
    .replace(/^\uFEFF/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
    .trim();
}

function detectDelimiter(headerLine: string): string {
  const candidates = [',', ';', '\t'];
  return candidates.reduce((best, candidate) =>
    headerLine.split(candidate).length > headerLine.split(best).length ? candidate : best
  );
}

function parseDelimitedText(text: string): Matrix {
  const cleanText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const firstLine = cleanText.split('\n')[0] ?? '';
  const delimiter = detectDelimiter(firstLine);
  const rows: Matrix = [];
  let current = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < cleanText.length; i++) {
    const char = cleanText[i];
    const next = cleanText[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      row.push(current.trim());
      current = '';
      continue;
    }

    if (char === '\n' && !inQuotes) {
      row.push(current.trim());
      rows.push(row);
      row = [];
      current = '';
      continue;
    }

    current += char;
  }

  row.push(current.trim());
  rows.push(row);
  return rows.filter((items) => items.some((item) => item.trim().length > 0));
}

function elementsByLocalName(root: Document | Element, name: string): Element[] {
  return Array.from(root.getElementsByTagName('*')).filter((element) => element.localName === name);
}

function readCellValue(cell: Element, sharedStrings: string[]): string {
  const type = cell.getAttribute('t');
  if (type === 'inlineStr') {
    return elementsByLocalName(cell, 't').map((node) => node.textContent ?? '').join('');
  }

  const value = elementsByLocalName(cell, 'v')[0]?.textContent ?? '';
  if (type === 's') {
    const index = Number(value);
    return Number.isInteger(index) ? sharedStrings[index] ?? '' : '';
  }
  return value;
}

async function readSharedStrings(zip: JSZip): Promise<string[]> {
  const file = zip.file('xl/sharedStrings.xml');
  if (!file) return [];

  const xmlText = await file.async('text');
  const xml = new DOMParser().parseFromString(xmlText, 'application/xml');
  return elementsByLocalName(xml, 'si').map((node) =>
    elementsByLocalName(node, 't').map((textNode) => textNode.textContent ?? '').join('')
  );
}

async function parseXlsxFile(file: File): Promise<Matrix> {
  const zip = await JSZip.loadAsync(await readBlobAsArrayBuffer(file));
  const sheetFile =
    zip.file('xl/worksheets/sheet1.xml') ??
    zip.file(/xl\/worksheets\/sheet\d+\.xml$/)[0];
  if (!sheetFile) {
    throw new Error('Planilha XLSX sem aba de dados');
  }

  const [sheetText, sharedStrings] = await Promise.all([
    sheetFile.async('text'),
    readSharedStrings(zip),
  ]);
  const xml = new DOMParser().parseFromString(sheetText, 'application/xml');
  const rows: Matrix = [];

  elementsByLocalName(xml, 'row').forEach((rowNode) => {
    const values: string[] = [];
    elementsByLocalName(rowNode, 'c').forEach((cell) => {
      const ref = cell.getAttribute('r') ?? 'A1';
      values[columnIndex(ref)] = readCellValue(cell, sharedStrings).trim();
    });
    rows.push(values.map((value) => value ?? ''));
  });

  return rows.filter((items) => items.some((item) => item.trim().length > 0));
}

function validateRow(row: EntitlementUploadRow): EntitlementUploadRow {
  const errors: string[] = [];
  const cpfDigits = row.cpf.replace(/\D/g, '');
  const cnpjDigits = row.cnpj.replace(/\D/g, '');

  if (!row.cpf.trim()) {
    errors.push('CPF obrigatório');
  } else if (cpfDigits.length !== 11) {
    errors.push('CPF deve ter 11 dígitos');
  }

  if (!row.cnpj.trim()) {
    errors.push('CNPJ obrigatório');
  } else if (cnpjDigits.length !== 14) {
    errors.push('CNPJ deve ter 14 dígitos');
  }

  if (!Number.isSafeInteger(row.quantity) || row.quantity < 1) {
    errors.push('Quantidade deve ser um inteiro positivo seguro');
  }

  return { ...row, errors };
}

function uploadColumnsFromMatrix(matrix: Matrix) {
  if (matrix.length === 0) {
    throw new Error('Arquivo não contém linhas de concessão');
  }

  const headers = matrix[0].map(normalizedHeader);
  const cpfIndex = headers.findIndex((header) => header === 'cpf');
  const cnpjIndex = headers.findIndex((header) => header === 'cnpj');
  const comboIndex = headers.findIndex((header) =>
    ['combo', 'codigocombo', 'codigodocombo', 'nomecombo', 'nomedocombo'].includes(header)
  );
  const quantityIndex = headers.findIndex((header) => ['quantidade', 'qtd'].includes(header));

  if (cpfIndex === -1 || cnpjIndex === -1) {
    throw new Error('Arquivo deve conter as colunas CNPJ e CPF');
  }

  return { cpfIndex, cnpjIndex, comboIndex, quantityIndex };
}

function parseQuantity(value: string): number {
  const quantity = value.trim();
  if (!quantity) return 1;
  if (!/^\d+$/.test(quantity)) return Number.NaN;
  const parsed = Number(quantity);
  return Number.isSafeInteger(parsed) && parsed >= 1 ? parsed : Number.NaN;
}

function dataRowsFromMatrix(matrix: Matrix): Matrix {
  uploadColumnsFromMatrix(matrix);
  const dataRows = matrix.slice(1)
    .filter((values) => values.some((value) => value.trim().length > 0));

  if (dataRows.length > ENTITLEMENT_UPLOAD_MAX_ROWS) {
    throw new Error(`Arquivo excede o limite de ${ENTITLEMENT_UPLOAD_MAX_ROWS} linhas`);
  }

  return dataRows;
}

function rowsFromMatrix(matrix: Matrix): EntitlementUploadRow[] {
  const { cpfIndex, cnpjIndex, comboIndex, quantityIndex } = uploadColumnsFromMatrix(matrix);
  const dataRows = dataRowsFromMatrix(matrix);

  return dataRows
    .map((values, index) => validateRow({
      cpf: values[cpfIndex]?.trim() ?? '',
      cnpj: values[cnpjIndex]?.trim() ?? '',
      combo: comboIndex >= 0 ? values[comboIndex]?.trim() ?? '' : '',
      quantity: parseQuantity(quantityIndex >= 0 ? values[quantityIndex]?.trim() ?? '' : ''),
      rowNumber: index + 2,
      errors: [],
    }))
    .filter((row) => row.cpf || row.cnpj || row.combo);
}

async function readEntitlementUploadMatrix(file: File): Promise<Matrix> {
  if (file.size === 0 || file.size > ENTITLEMENT_UPLOAD_MAX_FILE_SIZE_BYTES) {
    throw new Error('Arquivo excede o tamanho máximo de 2MB');
  }

  if (!isSupportedEntitlementUploadFile(file)) {
    throw new Error('Arquivo deve ser CSV ou XLSX');
  }

  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return extension === '.xlsx'
    ? parseXlsxFile(file)
    : parseDelimitedText(await readBlobAsText(file));
}

/**
 * Confere somente a estrutura que o browser precisa conhecer antes do upload.
 * Os valores e outcomes de cada linha ficam exclusivamente no processamento
 * do job no backend.
 */
export async function validateEntitlementUploadFile(file: File): Promise<number> {
  const matrix = await readEntitlementUploadMatrix(file);
  const rows = dataRowsFromMatrix(matrix);
  if (rows.length === 0) {
    throw new Error('Arquivo não contém linhas de concessão');
  }
  const invalidQuantity = rowsFromMatrix(matrix).find((row) => row.errors.some((error) => error.startsWith('Quantidade')));
  if (invalidQuantity) {
    throw new Error(`Linha ${invalidQuantity.rowNumber}: Quantidade deve ser um inteiro positivo seguro`);
  }
  return rows.length;
}

export async function parseEntitlementUploadFile(file: File): Promise<EntitlementUploadRow[]> {
  const rows = rowsFromMatrix(await readEntitlementUploadMatrix(file));
  if (rows.length === 0) {
    throw new Error('Arquivo não contém linhas de concessão');
  }

  return rows;
}
